<script setup lang="ts">
import { computed } from 'vue'
import { useTranslate } from '@tolgee/vue'
import { usePublicData } from '@/stores/PublicData'

import CheckMarkIcon from '@/components/icons/CheckMarkIcon.vue'
import { useEditorStore } from '@/stores/Editor'
import { BackgroundTheme } from '@/api/interfaces'
import { getCSSVariable } from '@/helpers/utils'
import BackgroundIcon from '@/components/icons/backgrounds/BackgroundIcon.vue'

const publicStore = usePublicData()
const editorStore = useEditorStore()
const { t } = useTranslate()

const list = [
  'PublicCTA.list.integrate',
  'PublicCTA.list.impact',
  'PublicCTA.list.plans',
  'PublicCTA.list.goals',
]

const listIconColor = computed(() => {
  if (editorStore.editor.background.theme === BackgroundTheme.DARK) {
    return getCSSVariable('darkest')
  }

  if (editorStore.editor.background.theme === BackgroundTheme.CUSTOM) {
    return editorStore.editor.background.themeColor.background
  }

  return getCSSVariable('ui-emerald-green')
})

const buttonColor = computed(() => {
  if (editorStore.editor.background.theme === BackgroundTheme.DARK) {
    return {
      backgroundColor: getCSSVariable('darkest'),
      textColor: getCSSVariable('lightest'),
    }
  }

  if (editorStore.editor.background.theme === BackgroundTheme.CUSTOM) {
    return {
      backgroundColor: editorStore.editor.background.themeColor.background,
      textColor: editorStore.editor.background.themeColor.text,
    }
  }

  return { backgroundColor: getCSSVariable('ui-emerald-green'), textColor: '#2d2d2d' }
})
</script>

<template>
  <section class="section public-cta">
    <BackgroundIcon class="background-icon" :icon-type="editorStore.editor.background.icon" />
    <v-container>
      <div class="public-cta-container">
        <img
          class="public-cta-image"
          src="@/assets/images/cta-img.webp"
          alt="man-with-dog-watching-sunset-under-lonely-tree"
          loading="lazy"
        />
        <div class="public-cta-content">
          <h2 class="public-cta-title">
            {{ $t('PublicCTA.title', { name: publicStore.name }) }}
          </h2>
          <ul class="public-cta-list">
            <li v-for="item in list" :key="item">
              <CheckMarkIcon :color="listIconColor" /> {{ t(item) }}
            </li>
          </ul>
          <div class="d-flex d-sm-block justify-center w-100">
            <GsButton
              href="https://www.getgreenspark.com/account-type"
              target="_blank"
              width="250"
              :style="{
                backgroundColor: buttonColor.backgroundColor,
                color: buttonColor.textColor,
              }"
            >
              {{ $t('PublicCTA.action') }}
            </GsButton>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<style lang="scss" scoped>
.section {
  position: relative;

  :deep(.background-icon) {
    position: absolute;
    display: none;

    @include media('md-and-up') {
      display: block;
      top: 50%;
      left: -50px;
      z-index: 0;
      width: 350px;
      height: 280px;
      transform: rotate(-25deg) translateY(-50%);
    }
  }
}

.public-cta-container {
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: 1;
  background: rgba(255, 255, 255, 0.4);

  .dark & {
    background: rgba(255, 255, 255, 0.2);
  }

  @include media('md-and-up') {
    flex-direction: row;
  }

  .btn {
    @include media('xs-only') {
      margin: auto;
    }
  }
}

.public-cta-image {
  max-width: 100%;
  object-fit: cover;
  max-height: 300px;

  @include media('md-and-up') {
    max-width: 440px;
    max-height: 100%;
  }
}

.public-cta-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;

  @include media('md-and-up') {
    padding: 34px 32px 34px 64px;
  }
}

.public-cta-title {
  font-size: 24px;
  font-weight: 600;

  @include media('md-and-up') {
    font-size: 36px;
  }
}

.public-cta-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0 32px 0;
  padding: 0;
  list-style-type: none;

  li {
    font-size: 20px;
    display: flex;

    svg {
      margin-right: 5px;
    }

    @include media('md-and-up') {
      font-size: 24px;
    }
  }
}
</style>
